import {
  Dashboard,
  Groups,
  School,
  Sensors,
  TrendingUp,
  WarningAmber
} from "@mui/icons-material";
import { Avatar, Box, Chip, CircularProgress, Grid2, Paper, Stack, Tab, Tabs, Typography, useTheme } from "@mui/material";
import { ResponsiveContainer, AreaChart, CartesianGrid, XAxis, YAxis, Area, Tooltip } from "recharts";

interface GlobalDashboardProps {
  state: any;
}

export function GlobalDashboard({ state }: GlobalDashboardProps) {
  const theme = useTheme();
  const { globalDashboardData, isFetchingGlobalDashboard } = state;

  if (isFetchingGlobalDashboard || !globalDashboardData) {
    return (
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const GLOBAL_KPIS = [
    { title: "Alunos na Escola", value: globalDashboardData.kpis.studentsInSchool, info: "em tempo real", type: "primary", icon: <Sensors /> },
    { title: "Frequência do Mês", value: globalDashboardData.kpis.monthFrequency, info: globalDashboardData.kpis.monthFrequencyTrend, type: "warning", icon: <TrendingUp /> },
    { title: "Atrasos Hoje", value: globalDashboardData.kpis.latesToday, info: globalDashboardData.kpis.latesTodayInfo, type: "error", icon: <WarningAmber /> },
    { title: "Pais Notificados", value: globalDashboardData.kpis.parentsNotified, info: globalDashboardData.kpis.parentsNotifiedInfo, type: "success", icon: <Groups /> },
  ];

  const expected = globalDashboardData.realTimeStatus.totalExpected;
  const inside = globalDashboardData.realTimeStatus.insideSchool;
  const donutPercentage = expected > 0 ? Math.round((inside / expected) * 100) : 0;

  const chartData = globalDashboardData.frequencyHistory.map((day: any) => {
    const total = day.presences + day.absences;
    const freq = total > 0 ? (day.presences / total) * 100 : 0;
    return {
      date: day.date,
      frequency: Number(freq.toFixed(1))
    };
  });

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      
      {/* HEADER DO GLOBAL */}
      <Box sx={{
        px: { xs: 2, md: 2 }, 
        pt: { xs: 2, md: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: 2
      }}>
        <Box sx={{ mb: { xs: 0, sm: 1 } }}>
          <Typography variant="h4" fontWeight="bold" color="text.primary">
            Visão Geral da Escola
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitoramento em tempo real de frequência, acessos e notificações.
          </Typography>
        </Box>
        <Tabs
          value="dashboard"
          textColor="primary"
          indicatorColor="primary"
          sx={{ minHeight: 48 }}
        >
          <Tab
            value="dashboard"
            icon={<Dashboard sx={{ fontSize: 20, mr: 1 }} />}
            iconPosition="start"
            label="Dashboard Geral"
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
          />
        </Tabs>
      </Box>

      {/* ÁREA DE CONTEÚDO */}
      <Box 
        role="tabpanel"
        sx={{ 
          pt: { xs: 2, md: 4 },
          pl: { xs: 2, md: 2 },
          pr: { xs: 2, md: 2 },
          pb: 0, 
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0
        }}
      >
        <Grid2 container spacing={3} alignItems="stretch">
          
          {/* KPIS GLOBAIS */}
          {GLOBAL_KPIS.map((kpi, idx) => (
            <Grid2 key={idx} size={{ xs: 12, sm: 6, lg: 3 }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'text.secondary', mb: 1 }}>
                  <Typography variant="body2" fontWeight="medium">{kpi.title}</Typography>
                  <Box sx={{ color: `${kpi.type}.main` }}>{kpi.icon}</Box>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="text.primary">{kpi.value}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">{kpi.info}</Typography>
              </Paper>
            </Grid2>
          ))}

          {/* GRÁFICO HISTÓRICO PERCENTUAL (Esquerda) */}
          <Grid2 size={{ xs: 12, lg: 8 }}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', minHeight: 360, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">Evolução da Frequência (Últimos 30 dias)</Typography>
                <Stack direction="row" spacing={2}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }} /> % de Frequência
                  </Typography>
                </Stack>
              </Box>
              
              <Box sx={{ 
                flex: 1, 
                position: 'relative', 
                minHeight: 250,
                '&:focus, &:active, & *:focus': { outline: 'none !important' } 
              }}>
                {chartData.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                    Sem dados de frequência para os últimos 30 dias.
                  </Typography>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={chartData}
                      margin={{ top: 10, right: 10, left: 0, bottom: 0 }} // Ajustei a margem esquerda (left) para dar espaço
                      style={{ outline: 'none' }}
                    >
                      <defs>
                        <linearGradient id="colorFreq" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
                      
                      <XAxis 
                        dataKey="date" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} 
                        dy={10}
                        minTickGap={20}
                      />
                      
                      <YAxis 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} 
                        tickFormatter={(value) => `${value}%`}
                        domain={[0, 100]}
                        ticks={[0, 25, 50, 75, 100]}
                        width={45}
                      />
                      
                      <Tooltip 
                        contentStyle={{ 
                          borderRadius: 8, 
                          border: `1px solid ${theme.palette.divider}`,
                          backgroundColor: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                          boxShadow: theme.shadows[3]
                        }}
                        itemStyle={{ fontWeight: 'bold' }}
                        labelStyle={{ color: theme.palette.text.secondary, marginBottom: 4 }}
                        formatter={((value: any) => [`${value}%`, 'Frequência']) as any}
                      />

                      <Area 
                        type="monotone"
                        dataKey="frequency" 
                        name="Frequência" 
                        stroke={theme.palette.primary.main} 
                        strokeWidth={3}
                        fillOpacity={1} 
                        fill="url(#colorFreq)" 
                        activeDot={{ strokeWidth: 0, r: 6 }} 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Paper>
          </Grid2>

          {/* RANKING DE TURMAS (Direita) */}
          <Grid2 size={{ xs: 12, lg: 4 }}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', minHeight: 360, maxHeight: 360, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" fontWeight="bold" color="text.primary" mb={1}>Frequência por Turma</Typography>
              
              {/* 🔥 Stack com rolagem interna (overflowY) e customização da Scrollbar */}
              <Stack 
                spacing={2.5} 
                sx={{ 
                  mt: 2, 
                  flex: 1, 
                  overflowY: 'auto', 
                  pr: 1, // Padding para o conteúdo não grudar na barra
                  // Customização da barra de rolagem invisível/fina
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-track': { background: 'transparent' },
                  '&::-webkit-scrollbar-thumb': { 
                    backgroundColor: 'divider', 
                    borderRadius: '4px' 
                  },
                  '&::-webkit-scrollbar-thumb:hover': { 
                    backgroundColor: 'action.disabled' 
                  }
                }}
              >
                {globalDashboardData.classRanking.map((item: any, i: number) => (
                  <Box key={i}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium" color="text.primary">{item.name}</Typography>
                      <Typography variant="body2" fontWeight="bold" color={item.color}>{item.freq}%</Typography>
                    </Box>
                    <Box sx={{ width: '100%', height: 6, bgcolor: 'divider', borderRadius: 4 }}>
                       <Box sx={{ width: `${item.freq}%`, height: '100%', bgcolor: item.color, borderRadius: 4 }} />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid2>

          {/* STATUS TEMPO REAL DA ESCOLA (Donut) */}
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle1" fontWeight="bold" color="text.primary" mb={3}>
                Resumo em Tempo Real
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                 <Box sx={{ position: 'relative', width: 140, height: 140, mx: 'auto' }}>
                    <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%' }}>
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#1e2e46" strokeWidth="3" />
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#3b82f6" strokeWidth="4" strokeDasharray={`${donutPercentage}, 100`} />
                    </svg>
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                      <Typography variant="h5" fontWeight="bold" color="text.primary" lineHeight={1.2}>{globalDashboardData.kpis.studentsInSchool}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>na escola</Typography>
                    </Box>
                 </Box>
              </Box>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Dentro da escola</Typography>
                  <Typography variant="caption" fontWeight="bold" color="text.primary">{globalDashboardData.realTimeStatus.insideSchool}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Já saíram hoje</Typography>
                  <Typography variant="caption" fontWeight="bold" color="text.primary">{globalDashboardData.realTimeStatus.alreadyLeft}</Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid2>

          {/* ALUNOS EM RISCO DE EVASÃO */}
          <Grid2 size={{ xs: 12, md: 8 }}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <School color="primary" fontSize="small" /> Alunos em Risco de Evasão
                </Typography>
              </Box>
              
              {globalDashboardData.evasionRisk.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                  Nenhum aluno em risco crítico no momento.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {globalDashboardData.evasionRisk.map((student: any) => (
                    <Box key={student.id} sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                       <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Avatar sx={{ width: 40, height: 40, bgcolor: 'divider', color: 'text.primary', fontWeight: 'bold' }}>
                            {student.initials}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold" color="text.primary">{student.name}</Typography>
                            <Typography variant="caption" color="text.secondary">Turma: {student.className}</Typography>
                          </Box>
                       </Box>
                       <Box sx={{ display: 'flex', alignItems: 'center' }}>
                         <Chip 
                           label={`Risco ${student.risk}`} 
                           size="small" 
                           color={student.riskColor as any} 
                           sx={{ borderRadius: 1, fontWeight: 'bold' }} 
                         />
                       </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid2>

        </Grid2>
      </Box>
    </Box>
  );
}